/**
 * Shared MCP auth-expiry handling (#389).
 *
 * Every service that calls the mcp-server (`/api/mcp/tools/*`) attaches the
 * current Supabase access token itself and gets back a plain `fetch`
 * Response. Before this module existed, a 401 from an expired/invalid token
 * was just turned into `{ data: null, error }` by each call site — the UI
 * showed a generic "action failed" message (or nothing) while the user
 * stayed on the page looking "logged in". Nothing ever forced a re-login.
 *
 * `handleMcpAuthExpiry()` is the single place that reacts to that 401.
 * Rather than nuking the session on the first 401 (a single request can come
 * back 401 for reasons that don't mean "session is dead" — e.g. the
 * mcp-server and supabase-js's background `autoRefreshToken` racing each
 * other right around expiry), it first tries ONE explicit
 * `supabase.auth.refreshSession()`:
 *   - refresh succeeds → the session is alive again; do nothing further.
 *     The failed request's own error handling still runs at the call site,
 *     but the user stays logged in and the NEXT call carries a fresh token.
 *   - refresh fails (or there's no session to refresh) → the refresh token
 *     itself is dead, so there's no point limping along. Sign the
 *     (already-invalid) Supabase session out directly, which fires a
 *     natural `SIGNED_OUT` auth event. AuthContext already distinguishes an
 *     intentional signOut() (via its own `intentionalSignOutRef`) from this
 *     kind of externally-forced one, so this path flips `sessionExpired` to
 *     true and SessionExpiryWatcher (mounted in App.js) takes it from
 *     there: a toast + redirect to /login with the expiry flag, exactly
 *     once.
 *
 * SECURITY NOTE: the logout path must call `supabase.auth.signOut()`
 * directly, NOT `AuthContext.signOut()` — the context's wrapper marks the
 * sign-out as intentional, which would suppress the session-expired flag
 * and silently swallow the very failure this module exists to surface.
 *
 * @module services/mcpClient
 */

import { supabase } from './supabase';

// Coalesces a burst of concurrent 401s (several in-flight MCP calls failing
// at once) into a single refresh-then-maybe-signOut cycle instead of one per
// response. Every 401 that lands while a cycle is already running awaits the
// SAME in-flight promise/outcome rather than kicking off its own.
let expiryCycleInFlight = null;

/**
 * Attempts a single Supabase session refresh. Never throws — a rejected
 * refreshSession() call (e.g. network down, refresh token revoked) is
 * treated the same as `{ error }`: refresh failed.
 *
 * @returns {Promise<boolean>} true if the refresh produced a usable session.
 */
const attemptSessionRefresh = async () => {
  try {
    const { data, error } = await supabase.auth.refreshSession();
    return Boolean(!error && data?.session);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[MCP] refreshSession after 401 failed:', err?.message);
    return false;
  }
};

/**
 * Force a clean logout after a Supabase refresh attempt also failed. Never
 * throws — best-effort, since even if the network call to revoke fails, the
 * local session/localStorage is still cleared by the supabase-js client and
 * the SIGNED_OUT event still fires.
 *
 * `scope: 'local'` por el mismo motivo que `authService.signOut`: el default
 * (`global`) revoca todas las sesiones del usuario, así que un 401 del
 * mcp-server en ESTA pestaña desconectaba el connector de ChatGPT. Acá además
 * es más claro todavía: lo que se detectó es que la sesión de este navegador ya
 * no sirve, y eso no dice nada sobre las otras.
 */
const forceLogout = async () => {
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[MCP] signOut after failed refresh (non-blocking):', err?.message);
  }
};

/**
 * React to a 401 from the mcp-server: try to refresh the Supabase session
 * once, and force a logout only if that refresh doesn't produce a usable
 * session. Idempotent/coalesced within a burst of concurrent 401s — safe to
 * call from multiple services at once; every concurrent caller shares the
 * same refresh attempt and (if needed) the same signOut() call.
 *
 * @returns {Promise<{refreshed: boolean}>} resolves once the cycle settles.
 *   `refreshed: true` means the session is alive again (no logout forced);
 *   `refreshed: false` means the session was dead and a logout was forced.
 */
export const handleMcpAuthExpiry = () => {
  if (expiryCycleInFlight) return expiryCycleInFlight;

  expiryCycleInFlight = (async () => {
    const refreshed = await attemptSessionRefresh();
    if (!refreshed) {
      await forceLogout();
    }
    return { refreshed };
  })().finally(() => {
    expiryCycleInFlight = null;
  });

  return expiryCycleInFlight;
};

/**
 * Convenience guard for call sites: pass the `fetch` Response from an MCP
 * call and it triggers the forced logout if (and only if) it was a 401.
 * Returns the boolean so callers can short-circuit their own error handling
 * if desired (not required — existing `!res.ok` branches still fire).
 *
 * @param {Response} res
 * @returns {boolean} true when this was an auth-expiry response
 */
export const checkMcpAuthExpiry = (res) => {
  if (res?.status === 401) {
    handleMcpAuthExpiry();
    return true;
  }
  return false;
};

/**
 * Ejecuta una tool del mcp-server con el token de la sesión actual.
 *
 * Los WRITEs del frontend van por el mcp-server a propósito (mismo camino que
 * usa el agente): así el audit log, las notificaciones y las reglas de negocio
 * salen de UNA sola implementación en vez de una copia por carril — que es la
 * deriva de #353/#454 documentada en CLAUDE.md.
 *
 * Vive acá porque este módulo ya es el dueño del contrato con
 * `/api/mcp/tools/*` (el 401 de #389 se maneja arriba).
 *
 * ES LA ÚNICA IMPLEMENTACIÓN. Hasta esta consolidación había siete copias
 * privadas de este mismo `fetch` (purchaseOrderService, userService,
 * tool{Assignment,Category,Dashboard,Location,Request}Service) y NO eran
 * idénticas: tres no tenían try/catch y rechazaban en vez de devolver
 * `{error}`, cinco perdían el fallback a `payload.data`, y cada una tenía su
 * propio texto de error. Nadie eligió esas diferencias — son deriva de
 * copiar-pegar. Si un call site necesita algo que esto no hace, se extiende
 * ACÁ; un fork "por ahora" es exactamente como nacieron las siete.
 *
 * Excepción legítima y única: `toolAssignmentService.returnTool` decide
 * ANTES de llamar si hay mcp-server configurado, porque sin él cae a un write
 * directo a Supabase en vez de fallar. Ese guard vive en el call site.
 *
 * @param {string} toolName - nombre registrado en mcpDiscovery (snake_case)
 * @param {Object} args - argumentos de la tool
 * @returns {Promise<{data: any, message: string|null, error: Error|null}>}
 */
export const callMcpTool = async (toolName, args = {}) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { data: null, message: null, error: new Error('Not authenticated') };
    }

    const mcpUrl = process.env.REACT_APP_MCP_SERVER_URL;
    if (!mcpUrl) {
      return {
        data: null,
        message: null,
        error: new Error('REACT_APP_MCP_SERVER_URL not configured'),
      };
    }

    const res = await fetch(`${mcpUrl}/api/mcp/tools/${toolName}/execute`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(args),
    });

    checkMcpAuthExpiry(res); // #389: un 401 fuerza re-login, no falla en silencio

    const payload = await res.json();
    if (!res.ok || !payload.success) {
      return {
        data: null,
        message: null,
        error: new Error(payload.error || `${toolName} failed`),
      };
    }

    return {
      data: payload.result?.data ?? payload.data ?? null,
      message: payload.result?.message || payload.message || null,
      // Lo que la tool NO hizo, dicho aparte del `message` (que es inglés de
      // máquina): approve_material_request lo devuelve para una externa de un
      // ítem (#783). Siempre array, para que el llamador no distinga "no hubo
      // nada que avisar" de "esta tool no avisa".
      notices: Array.isArray(payload.result?.notices)
        ? payload.result.notices
        : (Array.isArray(payload.notices) ? payload.notices : []),
      error: null,
    };
  } catch (error) {
    return { data: null, message: null, error };
  }
};

/**
 * GET autenticado contra el mcp-server, para endpoints que NO son tools
 * (`/api/atlas/graph`). Misma sesion, mismo manejo de 401 (#389) y mismo
 * contrato `{ data, error }` que `callMcpTool`: se extiende ESTE modulo en vez
 * de abrir un `fetch` aparte, que es como nacieron las siete copias que
 * `callMcpTool` consolido.
 *
 * El cuerpo de error del servidor (`{ error: { message } }`) se sube al `Error`
 * para que la pantalla muestre algo accionable (p. ej. el 503 de "el build no
 * genero el grafo") y no un "failed" generico.
 *
 * @param {string} path - ruta con `/` inicial, p. ej. `/api/atlas/graph`
 * @returns {Promise<{data: any, status: number|null, error: Error|null}>}
 */
export const getMcpJson = async (path) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return { data: null, status: null, error: new Error('Not authenticated') };

    const mcpUrl = process.env.REACT_APP_MCP_SERVER_URL;
    if (!mcpUrl) {
      return { data: null, status: null, error: new Error('REACT_APP_MCP_SERVER_URL not configured') };
    }

    const res = await fetch(`${mcpUrl}${path}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    checkMcpAuthExpiry(res);

    let payload = null;
    try { payload = await res.json(); } catch (e) { payload = null; }
    if (!res.ok || !payload || payload.success === false) {
      const msg = (payload && payload.error && (payload.error.message || payload.error)) || `${path} failed (${res.status})`;
      return { data: null, status: res.status, error: new Error(typeof msg === 'string' ? msg : `${path} failed (${res.status})`) };
    }
    return { data: payload.data ?? null, status: res.status, error: null };
  } catch (error) {
    return { data: null, status: null, error };
  }
};
